import { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { FiShield, FiFlag } from 'react-icons/fi';
import { FaPlus } from 'react-icons/fa6';
import { IoIosRefresh } from 'react-icons/io';
import { toast } from 'react-toastify';
import NewTooltip from '../../atoms/NewTooltip';
import ThreeColumnLayout from '../../layouts/ThreeColumnLayout';
import FilterSidebar from '../../organisms/FilterSidebar';
import FilterSkeleton from '../../atoms/FilterSkeleton';
import ATable from '../../organisms/ATable';
import TableSkeleton from '../../atoms/TableSkeleton';
import Loading from '../../ions/Loading';
import TextEdit from '../../molecules/TextEdit';
import PageFooter from '../../molecules/PageFooter';
import { collateralApi, ApiError } from '../../../services/collateralApi';
import LoadReceivablesModal from './ReceivableModalForm';
import CreateAnalysisPageForm from './CreateAnalysisPageForm';
import ValidationStatusBadge from './ValidationStatus';
import {
    formatCurrency,
    formatPercentage,
} from './collateralUtils';

const STATUSES: { value: string; label: string }[] = [
    { value: 'CONCLUIDO', label: 'Concluído' },
    { value: 'PARCIALMENTE_VALIDADA', label: 'Parcialmente validada' },
    { value: 'NAO_INICIADO', label: 'Não iniciado' },
];

const ValidationPageList = () => {
    const navigate = useNavigate();
    const [refreshKey, setRefreshKey] = useState(0);
    const [showNewForm, setShowNewForm] = useState(false);
    const [statusFilter, setStatusFilter] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [sortField, setSortField] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
    const [priorityFilter, setPriorityFilter] = useState<'' | 'true' | 'false'>('');
    const [rows, setRows] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [apiInfo, setApiInfo] = useState({ version: '', environment: '' });
    const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
    const [priorityCounts, setPriorityCounts] = useState<{ priority: number; notPriority: number }>({ priority: 0, notPriority: 0 });
    const [loadReceivablesRow, setLoadReceivablesRow] = useState<any>(null);
    const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());
    const pageSize = 100;

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const result = await collateralApi.getValidations({
                page, pageSize,
                status: statusFilter || undefined,
                search: search || undefined,
                priority: priorityFilter || undefined,
                sortField: sortField || undefined,
                sortOrder: sortOrder || undefined,
            } as any);
            setRows(result.items || []);
            setTotal(result.total || 0);
            setApiInfo({ version: result.apiVersion || '', environment: result.environment || '' });
            setStatusCounts(result.statusCounts || {});
            setPriorityCounts(result.priorityCounts || { priority: 0, notPriority: 0 });
        } catch (e: any) {
            if (e instanceof ApiError) {
                if (e.status === 403) return navigate('/access-denied');
                if (e.status === 404) return navigate('/not-found');
            }
            navigate('/error');
        } finally {
            setIsLoading(false);
            setInitialLoading(false);
        }
    }, [page, statusFilter, search, priorityFilter, sortField, sortOrder]);

    useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

    const handleRowClick = (row: any) => {
        if (!row.totalReceivables) return;
        navigate(`/collateral/validation/${row.id}/receivables`);
    };

    const columns = [
        { id: 'op', title: 'Operação', field: 'operationCode', canSort: true, width: '120px' },
        { id: 'assignor', title: 'Cedente', field: 'assignor', canSort: true },
        { id: 'titulos', title: 'Títulos', field: 'totalReceivables', canSort: true, cssClass: 'text-center', width: '70px' },
        { id: 'sacados', title: 'Sacados', field: 'totalPayer', canSort: false, cssClass: 'text-center', width: '70px' },
        { id: 'amount', title: 'Valor', field: 'totalAmount', canSort: true, cssClass: 'text-right', width: '130px' },
        { id: 'payed', title: 'Valor Validado', field: 'validatedAmount', canSort: true, cssClass: 'text-right', width: '120px' },
        { id: 'balance', title: 'Saldo', field: 'balance', canSort: true, cssClass: 'text-right', width: '120px' },
        { id: 'percVal', title: '% Valid.', field: 'validatedPercentage', canSort: true, cssClass: 'text-right', width: '80px' },
        { id: 'status', title: 'Status', field: 'validationStatus', canSort: true, cssClass: 'text-center', width: '160px' },
    ];

    const handlePriorityToggle = (e: React.MouseEvent, row: any) => {
        e.stopPropagation();
        if (togglingIds.has(row.id)) return;
        const newPriority = !row.priority;
        setTogglingIds(prev => new Set(prev).add(row.id));
        // Optimistic update
        const shouldRemove = (priorityFilter === 'true' && !newPriority) || (priorityFilter === 'false' && newPriority);
        setRows(prev => shouldRemove
            ? prev.filter(r => r.id !== row.id)
            : prev.map(r => r.id === row.id ? { ...r, priority: newPriority } : r));
        if (shouldRemove) setTotal(t => Math.max(0, t - 1));
        setPriorityCounts(prev => ({
            priority: prev.priority + (newPriority ? 1 : -1),
            notPriority: prev.notPriority + (newPriority ? -1 : 1),
        }));
        collateralApi.updatePriority(row.id, newPriority)
            .catch(() => { setRefreshKey(k => k + 1); toast.error('Erro ao atualizar prioridade'); })
            .finally(() => setTogglingIds(prev => { const next = new Set(prev); next.delete(row.id); return next; }));
    };

    const filterGroups = initialLoading ? [] : [
        {
            title: '',
            children: (
                <div className="space-y-4">
                    <button onClick={() => { setStatusFilter(''); setPriorityFilter(''); }}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${!statusFilter && !priorityFilter
                            ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                        <div className="flex items-center justify-between">
                            <span>Todos</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">{Object.values(statusCounts).reduce((a, b) => a + b, 0)}</span>
                        </div>
                    </button>
                    <div className="border-t border-gray-200 dark:border-slate-700 pt-3">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-3">STATUS</div>
                        <div className="space-y-1">
                    {STATUSES.map(s => (
                        <button key={s.value} onClick={() => setStatusFilter(s.value)}
                            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${statusFilter === s.value
                                ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                            <div className="flex items-center justify-between">
                                <span>{s.label}</span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">{statusCounts[s.label] || 0}</span>
                            </div>
                        </button>
                    ))}
                        </div>
                    </div>
                    <div className="border-t border-gray-200 dark:border-slate-700 pt-3">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-3">PRIORIDADE</div>
                        <div className="space-y-1">
                            <button onClick={() => setPriorityFilter(f => f === 'true' ? '' : 'true')}
                                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${priorityFilter === 'true'
                                    ? 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 font-medium'
                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <FiFlag className="text-red-500" size={13} />
                                        <span>Prioritários</span>
                                    </div>
                                    <span className="text-xs text-gray-500 dark:text-gray-400">{priorityCounts.priority}</span>
                                </div>
                            </button>
                            <button onClick={() => setPriorityFilter(f => f === 'false' ? '' : 'false')}
                                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${priorityFilter === 'false'
                                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                                <div className="flex items-center justify-between">
                                    <span>Não prioritários</span>
                                    <span className="text-xs text-gray-500 dark:text-gray-400">{priorityCounts.notPriority}</span>
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
            ),
        },
    ];

    return (
        <>
            {isLoading && <Loading />}

            {!showNewForm && (
                <button onClick={() => setShowNewForm(true)}
                    className="fixed top-[69px] right-[10px] w-10 h-10 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 z-50 flex items-center justify-center border-2 border-white dark:border-slate-800 bg-blue-600 hover:bg-blue-700 text-white"
                    title="Nova Análise de Validação">
                    <FaPlus size={14} />
                </button>
            )}

            <ThreeColumnLayout
                leftSidebarTitle={
                    <div className="flex items-center justify-between w-full">
                        <div className="flex items-center space-x-2">
                            <FiShield className="text-blue-600" size={18} />
                            <span>Garantias</span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                            {apiInfo.version && <div>v{apiInfo.version}</div>}
                            <div className={`font-medium ${apiInfo.environment === 'prd' ? 'text-red-600' :
                                apiInfo.environment === 'hml' ? 'text-yellow-600' : 'text-green-600'}`}>
                                {(apiInfo.environment || '').toUpperCase()}
                            </div>
                        </div>
                    </div>
                }
                leftSidebar={
                    <FilterSidebar filterGroups={filterGroups}>
                        {initialLoading && <FilterSkeleton />}
                    </FilterSidebar>
                }
                showRightPanel={showNewForm}
                onCloseRightPanel={() => setShowNewForm(false)}
                rightPanel={showNewForm ? (
                    <div className="h-full p-6">
                        <CreateAnalysisPageForm
                            onClose={() => setShowNewForm(false)}
                            onCreated={() => { setShowNewForm(false); setRefreshKey(k => k + 1); toast.success('Análise criada'); }}
                        />
                    </div>
                ) : null}
            >
                <div className="flex flex-col h-full bg-white dark:bg-slate-800 relative overflow-hidden">
                    {/* Header */}
                    <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-800 z-10">
                        <div className="flex items-center justify-between">
                            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Validação de Sacados</h1>
                            <div className="flex items-center space-x-2">
                                <button onClick={() => setRefreshKey(k => k + 1)}
                                    className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                    title="Atualizar lista">
                                    <IoIosRefresh size={18} />
                                </button>
                                <div className="w-80">
                                    <TextEdit label="Buscar cessão ou cedente..." value={searchInput}
                                        onChange={e => setSearchInput(e.target.value)}
                                        onKeyDown={(e: any) => e.key === 'Enter' && setSearch(searchInput)} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="flex-1 overflow-auto [&_table]:table-fixed [&_table]:w-full">
                        {initialLoading ? (
                            <TableSkeleton rows={12} />
                        ) : (
                            <ATable headers={columns} fieldSorted={sortField} orderSorted={sortOrder} onSort={(f: string, o: 'ASC' | 'DESC') => { setSortField(f); setSortOrder(o); }}>
                                {rows.length === 0 ? (
                                    <tr><td colSpan={11} className="py-8 text-center text-gray-500 dark:text-gray-400">Nenhuma validação encontrada</td></tr>
                                ) : rows.map((row: any, index: number) => (
                                    <tr key={row.id}
                                        className={`group hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer ${index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-900'}`}
                                        onClick={() => handleRowClick(row)}>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm font-medium dark:text-white">
                                            <div className="flex items-center gap-1.5 truncate">
                                                <span className="truncate" title={row.operationCode}>{row.operationCode}</span>
                                                <button onClick={e => handlePriorityToggle(e, row)}
                                                    disabled={togglingIds.has(row.id)}
                                                    className={`ml-auto flex-shrink-0 p-0.5 rounded hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors ${row.priority ? 'text-red-500' : 'text-gray-300 opacity-0 group-hover:opacity-100'} ${togglingIds.has(row.id) ? 'opacity-30 pointer-events-none' : ''}`}
                                                    title={row.priority ? 'Remover prioridade' : 'Marcar como prioritário'}>
                                                    <FiFlag size={12} />
                                                </button>
                                            </div>
                                        </td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-white truncate" title={row.assignor}>{row.assignor}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center text-sm dark:text-white">{row.totalReceivables || '—'}
                                        </td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center text-sm dark:text-white">{row.totalPayer || '—'}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-right text-sm dark:text-white">{formatCurrency(row.totalAmount)}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-right text-sm dark:text-white">{formatCurrency(row.validatedAmount)}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-right text-sm dark:text-white">{formatCurrency(row.balance)}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-right text-sm dark:text-white">{formatPercentage(row.validatedPercentage)}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center">
                                            {!row.totalReceivables && (
                                                <NewTooltip content="Carregar recebíveis" alignment="top">
                                                    <button onClick={e => { e.stopPropagation(); setLoadReceivablesRow(row); }}
                                                        className="text-white py-1 px-3 rounded-lg text-xs bg-teal-500 hover:bg-teal-700">
                                                        Recebíveis
                                                    </button>
                                                </NewTooltip>
                                            )}
                                            {row.totalReceivables > 0 && (
                                                <ValidationStatusBadge status={row.validationStatus} />
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </ATable>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex-shrink-0 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                        <PageFooter currentPage={page - 1} totalPages={Math.ceil(total / pageSize)} itemsPerPage={pageSize} totalElements={total} onPageChange={(p) => setPage(p + 1)} />
                    </div>
                </div>
            </ThreeColumnLayout>

            {loadReceivablesRow && (
                <LoadReceivablesModal
                    analysisId={loadReceivablesRow.id}
                    operationCode={loadReceivablesRow.operationCode}
                    offerOperationId={loadReceivablesRow.offerOperationId}
                    onClose={() => setLoadReceivablesRow(null)}
                    onSuccess={() => { setLoadReceivablesRow(null); setRefreshKey(k => k + 1); toast.success('Recebíveis carregados'); }}
                />
            )}

            <Outlet context={{ onDataChange: () => setRefreshKey(k => k + 1) }} />
        </>
    );
};

export default ValidationPageList;
